/**
 * UnionLogo — renders either the official association logo (URL fed
 * by the admin/officer overlay or the seed default, always proxied
 * server-side to avoid CORS/hot-link issues) or an on-brand
 * monogram tile using the union's `color`.
 *
 * The monogram is the graceful fallback: we don't have logos for
 * every one of the 244+ unions, so a coloured 2-letter tile keeps
 * every card visually anchored.
 *
 * Usage:
 *   <UnionLogo assoc={r} size={48} />
 *   <UnionLogo assoc={r} size={72} rounded="lg" />
 */
import { useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function initials(name) {
  if (!name) return "?";
  const words = name.replace(/[·\-_/]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Resolve the backend logo path. When ``logo_url`` is already an
 * absolute URL we hand it through unchanged; when it's the relative
 * ``/api/public/associations/{id}/logo`` proxy path we prefix the
 * backend host so it works from any origin.
 */
function resolveLogoUrl(raw) {
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${BACKEND}${raw}`;
}

export default function UnionLogo({
  assoc,
  size = 48,
  rounded = "md",       // md | lg | xl | full
  className = "",
}) {
  // Broken URL → fall through to monogram. Tracked per-render so a
  // rotting URL never leaves an empty box on the page.
  const [broken, setBroken] = useState(false);
  const url = resolveLogoUrl(assoc?.logo_url);
  const hasLogo = !!url && !broken;
  const radius = {
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  }[rounded] || "rounded-md";

  const dims = { width: size, height: size };
  const tid = `union-logo-${assoc?.id || "unknown"}`;

  if (hasLogo) {
    return (
      <div
        className={`${radius} bg-white/95 flex items-center justify-center overflow-hidden shrink-0 ${className}`}
        style={dims}
        data-testid={tid}
        data-logo="official"
      >
        <img
          src={url}
          alt={`${assoc?.name || "Union"} logo`}
          onError={() => setBroken(true)}
          loading="lazy"
          className="max-w-[85%] max-h-[85%] object-contain"
        />
      </div>
    );
  }

  // Monogram fallback — pigmented tile using the union's brand colour.
  return (
    <div
      className={`${radius} flex items-center justify-center font-display font-bold text-white shrink-0 ${className}`}
      style={{
        ...dims,
        background: assoc?.color || "#4A4E51",
        boxShadow: `0 0 0 1px ${(assoc?.color || "#4A4E51")}55`,
        fontSize: Math.max(10, Math.round(size * 0.32)),
        letterSpacing: "0.04em",
      }}
      data-testid={tid}
      data-logo="monogram"
    >
      {initials(assoc?.name)}
    </div>
  );
}
